/* eslint-disable indent */
import React from 'react';

import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { dateObject } from 'utils';
import { decimalToPercent } from 'utils/converter';

// Components
import { Mui, TextField } from 'components';
import { Stack } from 'components/layouts';

// Local components
import EnrollmentPaymentSettings from './EnrollmentPaymentSettings';

const PAYMENT_PLANS = [
  { label: 'Selecione um plano de pagamento', value: '' },
  { label: 'Mensal', value: 'monthly' },
  { label: 'Parcela única', value: 'single-installment' },
  { label: 'Customizado', value: 'custom' },
];

const ENROLLMENT_PAYMENT_SETTINGS_PLANS = ['monthly', 'single-installment'];

const BILLING_START_DATE_TYPES = [
  { label: 'Fixa', value: 'fixed' },
  { label: 'Data de criação da matrícula', value: 'enrollment_creation_date' },
];

const FORM_MESSAGES = {
  name: { label: 'Nome', required: 'É necessário informar o nome' },
  installmentAmount: { label: 'Valor da parcela' },
  originalAmount: {
    label: 'Valor integral da negociação',
    required: 'É necessário informar o valor integral da negociação',
  },
  dueDay: {
    label: 'Dia do vencimento da(s) parcela(s)',
    min: 'Dia deve ser maior ou igual a 1',
    max: 'Dia deve ser menor ou igual a 30',
    required: 'É necessário informar o dia de vencimento',
  },
  dueDaySelectionEnabled: { label: 'Permitir que o responsável escolha o dia do vencimento' },
  cycle: { label: 'Ciclo' },
  paymentPlan: {
    label: 'Plano de pagamento',
    required: 'É necessário selecionar um plano de pagamento',
  },
  recurrence: {
    label: 'Quantidade de parcelas',
    min: 'Mínimo 1 parcela',
    helperText: 'Quantidade de parcelas que serão geradas',
  },
  recurrenceSelectionEnabled: {
    label: 'Permitir que o responsável escolha a quantidade de parcelas',
  },
  minimumFirstInstallmentDays: {
    label: 'Prazo mínimo',
    helperText: 'Informe 0 para não aplicar prazo mínimo',
    integer: 'O prazo deve ser um número inteiro',
    min: 'O prazo deve ser maior ou igual a 0',
    max: 'O prazo deve ser menor ou igual a 180',
    required: 'É necessário informar o prazo mínimo para o primeiro vencimento',
  },
  billingStartDate: {
    label: 'Data para começar a gerar os pagamentos',
    required: 'É necessário informar a data para começar a gerar os pagamentos',
    helperText: 'Informe a data para iniciar a geração da(s) cobrança(s).',
  },
  billingStartDateType: { label: 'Tipo de data de início da cobrança' },
  monthlyInterestRate: { label: 'Taxa de juros mensal' },
  latePaymentFinePercentage: { label: 'Multa por atraso percentual' },
  latePaymentFineFixed: { label: 'Multa por atraso fixa' },
  payableWith: { label: 'Métodos de pagamento disponíveis' },
  maxInstallments: {
    label: 'Máximo de parcelas',
    required: 'É necessário preencher o máximo de parcelas',
    min: 'Valor mínimo para o máximo de parcelas deve ser 1',
  },
  description: {
    label: 'Descrição dos pagamentos',
    helperText: 'Informações adicionais a serem incluídas nos pagamentos',
  },
  isActive: { label: 'Ativo?' },
};

function Basic({ isStaff, values, onSubmit, CycleSelect }) {
  // Constants
  const initialValues = {
    name: values?.name || '',
    installmentAmount: values?.originalAmount
      ? (Number(values.originalAmount) / Number(values?.recurrence || 1)).toFixed(2)
      : '',
    originalAmount: values?.originalAmount || '',
    dueDay: values?.dueDay || 5,
    dueDaySelectionEnabled: values?.dueDaySelectionEnabled || false,
    cycle: values?.cycle || new Date().getFullYear(),
    paymentPlan: values?.paymentPlan || '',
    recurrence: values?.recurrence || 1,
    recurrenceSelectionEnabled: values?.recurrenceSelectionEnabled || false,
    minimumFirstInstallmentDays: values?.minimumFirstInstallmentDays || 0,
    billingStartDate: values?.billingStartDate ? dateObject(values.billingStartDate) : '',
    billingStartDateType: values?.billingStartDateType || 'fixed',
    monthlyInterestRate: Number(values?.monthlyInterestRate)
      ? decimalToPercent(values.monthlyInterestRate)
      : 0,
    latePaymentFinePercentage: Number(values?.latePaymentFinePercentage)
      ? decimalToPercent(values.latePaymentFinePercentage)
      : 0,
    latePaymentFineFixed: values?.latePaymentFineFixed || 0,
    payableWith: values?.payableWith || [],
    maxInstallments: values?.maxInstallments || 1,
    description: values?.description || '',
    isActive: values ? values.isActive : true,
  };

  const yup = Yup.object().shape({
    name: Yup.string().required(FORM_MESSAGES.name.required),
    originalAmount: Yup.string().required(FORM_MESSAGES.originalAmount.required),
    dueDay: Yup.number()
      .min(1, FORM_MESSAGES.dueDay.min)
      .max(30, FORM_MESSAGES.dueDay.max)
      .required(FORM_MESSAGES.dueDay.required),
    dueDaySelectionEnabled: Yup.boolean(),
    cycle: Yup.number().nullable(),
    paymentPlan: Yup.string().nullable().required(FORM_MESSAGES.paymentPlan.required),
    recurrence: Yup.number().min(1, FORM_MESSAGES.recurrence.min),
    recurrenceSelectionEnabled: Yup.boolean(),
    minimumFirstInstallmentDays: Yup.number()
      .integer(FORM_MESSAGES.minimumFirstInstallmentDays.integer)
      .min(0, FORM_MESSAGES.minimumFirstInstallmentDays.min)
      .max(180, FORM_MESSAGES.minimumFirstInstallmentDays.max)
      .required(FORM_MESSAGES.minimumFirstInstallmentDays.required),
    billingStartDate: Yup.date()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .nullable()
      .when(['billingStartDateType', 'paymentPlan'], {
        is: (billingStartDateType, paymentPlan) =>
          billingStartDateType === 'fixed' && paymentPlan !== 'custom',
        then: schema => schema.required(FORM_MESSAGES.billingStartDate.required),
        otherwise: schema => schema.nullable(),
      }),
    billingStartDateType: Yup.string().nullable(),
    monthlyInterestRate: Yup.number().nullable(),
    latePaymentFinePercentage: Yup.number().nullable(),
    latePaymentFineFixed: Yup.number().nullable(),
    payableWith: Yup.array().nullable(),
    maxInstallments: Yup.number().when(['paymentPlan', 'payableWith'], {
      is: (paymentPlan, payableWith) =>
        paymentPlan === 'single-installment' && payableWith?.includes('credit_card'),
      then: schema =>
        schema
          .nullable()
          .min(1, FORM_MESSAGES.maxInstallments.min)
          .required(FORM_MESSAGES.maxInstallments.required),
      otherwise: schema => schema.nullable(),
    }),
    description: Yup.string().nullable(),
    isActive: Yup.boolean(),
  });

  const formik = useFormik({
    initialValues,
    onSubmit,
    validationSchema: yup,
    enableReinitialize: true,
  });

  // Actions
  const handleCheckPayableWith = (paymentMethod, checked) => {
    const selectedPayableWith = structuredClone(formik.values.payableWith);
    formik.setFieldTouched('payableWith', true);
    if (checked) {
      selectedPayableWith.push(paymentMethod);
      formik.setFieldValue('payableWith', [...selectedPayableWith], true);
    } else {
      formik.setFieldValue(
        'payableWith',
        selectedPayableWith.filter(item => item !== paymentMethod),
        true,
      );
    }
  };

  const handleChangePaymentPlan = e => {
    const { value } = e.target;
    formik.setValues(
      {
        ...formik.values,
        paymentPlan: value,
        installmentAmount: '',
        maxInstallments: '',
        recurrence: 1,
        ...(value === 'custom'
          ? {
              billingStartDate: '',
              dueDay: 1,
              dueDaySelectionEnabled: false,
              recurrenceSelectionEnabled: false,
              minimumFirstInstallmentDays: 0,
            }
          : {}),
      },
      true,
    );
  };

  const showMaxInstallments =
    formik.values.paymentPlan === 'single-installment' &&
    formik.values.payableWith?.includes('credit_card');
  const showBillingStartDate =
    formik.values.paymentPlan &&
    formik.values.paymentPlan !== 'custom' &&
    formik.values.billingStartDateType === 'fixed';
  const showEnrollmentPaymentSettings =
    isStaff && ENROLLMENT_PAYMENT_SETTINGS_PLANS.includes(formik.values.paymentPlan);

  return (
    <Stack>
      <TextField
        required
        name="name"
        type="text"
        placeholder={FORM_MESSAGES.name.label}
        formik={formik}
        messages={FORM_MESSAGES}
        disabled={formik.isSubmitting}
        margin="none"
      />
      <Stack
        layout={[
          { xs: 12, sm: 8 },
          { xs: 12, sm: 4 },
        ]}>
        <Mui.FormControl variant="outlined" fullWidth disabled={formik.isSubmitting}>
          <Mui.InputLabel id="paymentPlanLabel">{FORM_MESSAGES.paymentPlan.label}</Mui.InputLabel>
          <Mui.Select
            required
            labelId="paymentPlanLabel"
            label={FORM_MESSAGES.paymentPlan.label}
            id="paymentPlan"
            value={formik.values.paymentPlan || []}
            disabled={formik.isSubmitting}
            onChange={handleChangePaymentPlan}>
            {PAYMENT_PLANS.map(option => (
              <Mui.MenuItem key={option.value} value={option.value}>
                {option.label}
              </Mui.MenuItem>
            ))}
          </Mui.Select>
        </Mui.FormControl>
        {!!CycleSelect &&
          CycleSelect({
            formik,
            name: 'cycle',
            showAll: false,
            disabled: formik.isSubmitting,
            onChange: event => {
              formik.setFieldTouched('cycle', true);
              formik.setFieldValue('cycle', event.target.value);
            },
            onBlur: () => formik.setFieldTouched('cycle', true),
          })}
      </Stack>
      <Mui.Card variant="outlined">
        <Mui.CardHeader title={<Mui.Typography variant="h5">Valores</Mui.Typography>} />
        <Mui.CardContent>
          <Stack
            layout={[
              { xs: 12, sm: 6 },
              { xs: 12, sm: 6 },
              { xs: 12, sm: 6 },
              { xs: 12, sm: 6 },
              { xs: 12, sm: 6 },
              { xs: 12, sm: 6 },
              { xs: 12, sm: 6 },
              { xs: 12, sm: 6 },
            ]}>
            {formik.values.paymentPlan === 'monthly' && (
              <TextField
                name="recurrence"
                type="number"
                formik={formik}
                messages={FORM_MESSAGES}
                disabled={formik.isSubmitting}
                margin="none"
                inputProps={{ min: 1 }}
                onChange={e => {
                  const { value } = e.target;
                  formik.setFieldTouched('recurrence', true);
                  formik.setFieldValue('recurrence', value, true);

                  if (formik.values.installmentAmount > 0) {
                    formik.setFieldValue(
                      'originalAmount',
                      Number((Number(value) * Number(formik.values.installmentAmount)).toFixed(2)),
                    );
                  }
                }}
              />
            )}
            {formik.values.paymentPlan === 'monthly' && (
              <TextField
                name="installmentAmount"
                envelope="currency"
                placeholder={FORM_MESSAGES.installmentAmount.label}
                formik={formik}
                messages={FORM_MESSAGES}
                disabled={formik.isSubmitting}
                margin="none"
                onChange={e => {
                  const { value } = e.target;
                  formik.setFieldTouched('installmentAmount', true);
                  formik.setFieldValue('installmentAmount', value, true);

                  if (formik.values.recurrence > 0) {
                    formik.setFieldValue(
                      'originalAmount',
                      Number((Number(value) * Number(formik.values.recurrence)).toFixed(2)),
                    );
                  }
                }}
              />
            )}
            <TextField
              required
              name="originalAmount"
              envelope="currency"
              placeholder={FORM_MESSAGES.originalAmount.label}
              formik={formik}
              messages={FORM_MESSAGES}
              disabled={formik.isSubmitting}
              margin="none"
              onChange={e => {
                const { value } = e.target;
                formik.setFieldTouched('originalAmount', true);
                formik.setFieldValue('originalAmount', value, true);

                if (formik.values.recurrence > 0) {
                  formik.setFieldValue(
                    'installmentAmount',
                    Number((Number(value) / Number(formik.values.recurrence)).toFixed(2)),
                  );
                }
              }}
            />
            {formik.values.paymentPlan && formik.values.paymentPlan !== 'custom' && (
              <TextField
                required
                name="dueDay"
                type="number"
                placeholder={FORM_MESSAGES.dueDay.label}
                formik={formik}
                messages={FORM_MESSAGES}
                disabled={formik.isSubmitting}
                margin="none"
                inputProps={{ min: 1, max: 30 }}
              />
            )}
            {formik.values.paymentPlan && formik.values.paymentPlan !== 'custom' && (
              <TextField
                select
                name="billingStartDateType"
                formik={formik}
                messages={FORM_MESSAGES}
                value={formik.values.billingStartDateType}
                disabled={formik.isSubmitting}
                margin="none"
                onChange={event => {
                  const { value } = event.target;
                  if (value !== 'fixed') {
                    formik.setTouched({ billingStartDate: true, billingStartDateType: true });
                    formik.setValues(
                      { ...formik.values, billingStartDate: null, billingStartDateType: value },
                      true,
                    );
                  } else {
                    formik.setFieldTouched('billingStartDateType', true);
                    formik.setFieldValue('billingStartDateType', value, true);
                  }
                }}
                SelectProps={{ native: true }}>
                <option value=""></option>
                {BILLING_START_DATE_TYPES.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </TextField>
            )}
            {showBillingStartDate && (
              <TextField
                required
                name="billingStartDate"
                envelope="date"
                formik={formik}
                messages={FORM_MESSAGES}
                disabled={formik.isSubmitting}
                margin="none"
              />
            )}
          </Stack>
        </Mui.CardContent>
      </Mui.Card>
      {showEnrollmentPaymentSettings && (
        <EnrollmentPaymentSettings formik={formik} formMessages={FORM_MESSAGES} />
      )}
      <Mui.Card variant="outlined">
        <Mui.CardHeader title={<Mui.Typography variant="h5">Formas de pagamento</Mui.Typography>} />
        <Mui.CardContent>
          <Stack>
            <Mui.Box>
              <Mui.Typography variant="body1">{FORM_MESSAGES.payableWith.label}</Mui.Typography>
              <Mui.FormControlLabel
                control={
                  <Mui.Switch
                    color="primary"
                    checked={formik.values.payableWith.includes('billing_note')}
                    onChange={event => handleCheckPayableWith('billing_note', event.target.checked)}
                    disabled={formik.isSubmitting}
                  />
                }
                label="Boleto bancário"
              />
              <Mui.FormControlLabel
                control={
                  <Mui.Switch
                    color="primary"
                    checked={formik.values.payableWith.includes('credit_card')}
                    onChange={event => handleCheckPayableWith('credit_card', event.target.checked)}
                    disabled={formik.isSubmitting}
                  />
                }
                label="Cartão de crédito"
              />
              <Mui.FormControlLabel
                control={
                  <Mui.Switch
                    color="primary"
                    checked={formik.values.payableWith.includes('pix')}
                    onChange={event => handleCheckPayableWith('pix', event.target.checked)}
                    disabled={formik.isSubmitting}
                  />
                }
                label="Pix"
              />
            </Mui.Box>
            {showMaxInstallments && (
              <TextField
                required
                name="maxInstallments"
                type="number"
                formik={formik}
                messages={FORM_MESSAGES}
                disabled={formik.isSubmitting}
                margin="none"
              />
            )}
            <TextField
              multiline
              minRows={4}
              name="description"
              formik={formik}
              messages={FORM_MESSAGES}
              disabled={formik.isSubmitting}
              margin="none"
            />
          </Stack>
        </Mui.CardContent>
      </Mui.Card>
      <Mui.Card variant="outlined">
        <Mui.CardHeader title={<Mui.Typography variant="h5">Taxas</Mui.Typography>} />
        <Mui.CardContent>
          <Stack
            layout={[
              { xs: 12 },
              { xs: 12, sm: 6 },
              { xs: 12 },
              { xs: 12, sm: 6 },
              { xs: 12, sm: 6 },
            ]}>
            <Mui.Typography variant="body1">Juros mensal</Mui.Typography>
            <TextField
              name="monthlyInterestRate"
              envelope="percentage"
              formik={formik}
              messages={FORM_MESSAGES}
              disabled={formik.isSubmitting}
              margin="none"
              inputProps={{ min: 0, max: 100, step: 0.01 }}
            />
            <Mui.Typography variant="body1">Multa por atraso</Mui.Typography>
            <TextField
              name="latePaymentFinePercentage"
              envelope="percentage"
              formik={formik}
              messages={FORM_MESSAGES}
              disabled={formik.isSubmitting}
              margin="none"
              inputProps={{ min: 0, max: 100, step: 0.01 }}
            />
            <TextField
              name="latePaymentFineFixed"
              envelope="currency"
              formik={formik}
              messages={FORM_MESSAGES}
              disabled={formik.isSubmitting}
              margin="none"
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Stack>
        </Mui.CardContent>
      </Mui.Card>
      <Mui.FormControlLabel
        control={
          <Mui.Switch
            color="primary"
            checked={!!formik.values.isActive}
            onChange={event => {
              formik.setFieldTouched('isActive', true);
              formik.setFieldValue('isActive', event.target.checked, true);
            }}
            disabled={formik.isSubmitting}
          />
        }
        label={formik.values.isActive ? 'Ativo' : 'Inativo'}
      />
      <Mui.Box display="flex" justifyContent="flex-end">
        <Mui.Button
          color="primary"
          variant="contained"
          size="large"
          loading={formik.isSubmitting}
          disabled={!formik.isValid || !formik.dirty || formik.isSubmitting}
          onClick={() => formik.handleSubmit()}>
          {values ? 'Salvar' : 'Cadastrar'}
        </Mui.Button>
      </Mui.Box>
    </Stack>
  );
}

Basic.propTypes = {
  isStaff: PropTypes.bool,
  values: PropTypes.object,
  onSubmit: PropTypes.func,
  CycleSelect: PropTypes.func,
};

export default Basic;
